import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { toast } from 'react-toastify';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import AddEditForm from '../../../sharedComponents/AddEditForm';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import useManageTravelCard from './useManageTravelCard';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '500px',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
  },
};

const registerItems = [
  { title: 'Travel Card Name', name: 'cardName', type: 'text' },
  { title: 'isActive', name: 'isActive', type: 'boolean' },
  { title: 'Fee JPY / 100k', name: 'feeJpyPer100k', type: 'number', step: 0.01 },
  { title: 'Fee THB / 100k', name: 'feeThbPer100k', type: 'number', step: 0.01 },
  { title: 'Auto Withdraw Fee on Exchange', name: 'autoWithdrawFee', type: 'boolean' },
];

const editItems = registerItems;

const deleteConfirmItems = [
  { title: 'Travel Card Name', name: 'cardName', type: 'text' },
];

const TravelCardSetting = () => {
  const {
    travelCards,
    error,
    refreshTravelCards,
    addTravelCard,
    deleteTravelCard,
    updateTravelCard,
  } = useManageTravelCard();

  const initialValue = useMemo(
    () => ({
      uuid: '',
      cardName: '',
      isActive: true,
      feeJpyPer100k: 0,
      feeThbPer100k: 0,
      autoWithdrawFee: false,
    }),
    []
  );

  const [mode, setMode] = useState('register');
  const [modalTitle, setModalTitle] = useState('Register Travel Card');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selected, setSelected] = useState(initialValue);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelected((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const openForm = (isEdit, id) => {
    if (isEdit) {
      const row = travelCards.find((w) => w.uuid === id);
      if (row) {
        setSelected({
          uuid: row.uuid,
          cardName: row.cardName ?? '',
          isActive: row.isActive ?? true,
          feeJpyPer100k: row.feeJpyPer100k ?? 0,
          feeThbPer100k: row.feeThbPer100k ?? 0,
          autoWithdrawFee: row.autoWithdrawFee ?? false,
        });
      }
      setMode('edit');
      setModalTitle('Edit Travel Card');
    } else {
      setSelected(initialValue);
      setMode('register');
      setModalTitle('Register Travel Card');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => setIsFormOpen(false);

  const openConfirm = (id) => {
    const row = travelCards.find((w) => w.uuid === id);
    if (row) {
      setSelected({
        uuid: row.uuid,
        cardName: row.cardName ?? '',
        isActive: row.isActive ?? true,
        feeJpyPer100k: row.feeJpyPer100k ?? 0,
        feeThbPer100k: row.feeThbPer100k ?? 0,
      });
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => setIsConfirmOpen(false);

  const handleRegisterTravelCard = async (payload) => {
    try {
      const ok = await addTravelCard({
        cardName: payload.cardName,
        isActive: payload.isActive,
        feeJpyPer100k: payload.feeJpyPer100k,
        feeThbPer100k: payload.feeThbPer100k,
        autoWithdrawFee: payload.autoWithdrawFee,
      });
      if (ok) {
        toast.success('Travel Card registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleUpdateTravelCard = async () => {
    try {
      const ok = await updateTravelCard(selected.uuid, {
        cardName: selected.cardName,
        isActive: selected.isActive,
        feeJpyPer100k: selected.feeJpyPer100k,
        feeThbPer100k: selected.feeThbPer100k,
        autoWithdrawFee: selected.autoWithdrawFee,
      });

      if (ok) {
        toast.success('Travel Card updated successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  const handleDeleteTravelCard = async (uuidParam) => {
    try {
      const uuidToDelete = uuidParam || selected.uuid;
      const ok = await deleteTravelCard(uuidToDelete);
      if (ok) {
        toast.success('Delete Travel card successfully');
        closeConfirm();
      } else {
        toast.error('Something wrong.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed');
    }
  };

  useEffect(() => {
    if (error) toast.error(error?.response?.data?.message || error?.message || error);
  }, [error]);

  useEffect(() => {
    refreshTravelCards();
  }, []);

  return (
    <div>
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm
          items={mode === 'edit' ? editItems : registerItems}
          name={modalTitle}
          data={selected}
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleRegister={handleRegisterTravelCard}
          handleUpdate={handleUpdateTravelCard}
          stateValue={selected}
        />
      </Modal>

      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm
          items={deleteConfirmItems}
          data={selected}
          closeConfirm={closeConfirm}
          confirmFunction={handleDeleteTravelCard}
        />
      </Modal>

      <div className="px-4 sm:px-6 lg:px-8">
        <div className="mt-4 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-4xl">Travel Card Setting</h2>
            <button
              onClick={() => openForm(false, null)}
              className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded shadow-sm hover:shadow-md transition-all duration-200"
            >
              Add New Travel Card
            </button>
          </div>

          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                    No
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Card Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Fee THB / 100k
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Fee JPY / 100k
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Auto Withdraw Fee
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {travelCards.map((card, index) => (
                  <tr key={card?.uuid}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                      {index + 1}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {card?.cardName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {card?.isActive ? (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {card?.feeThbPer100k ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {card?.feeJpyPer100k ?? 0}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {card?.autoWithdrawFee ? (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-800">
                          On
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600">
                          Off
                        </span>
                      )}
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex items-center space-x-3 justify-end">
                        <PencilIcon
                          onClick={() => openForm(true, card?.uuid)}
                          className="cursor-pointer w-6 h-6 fill-green-600"
                        />
                        <TrashIcon
                          onClick={() => openConfirm(card?.uuid)}
                          className="cursor-pointer w-6 h-6 fill-red-600"
                        />
                      </div>
                    </td>
                  </tr>
                ))}

                {travelCards.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-6 text-sm text-gray-500">
                      No travel cards found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TravelCardSetting;